import { Box, Tooltip } from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTrafficLight } from "@fortawesome/pro-regular-svg-icons";
import { ServerSideDataGridModel } from "src/components/common/dataGrid/gridModel/ServerSideDataGridModel";
import { SalesLeadsApi } from "src/api/generated/erp/sales/crm/salesLeads/api/salesLeadsApi.ts";
import { SalesLeadView } from "src/api/generated/erp/db/types/tables/salesLeadView.ts";
import { FormCrudDataGrid } from "src/components/common/dataGrid/crud/FormCrudDataGrid";
import { dateTimeColumn, enumColumn, textColumn } from "src/components/common/dataGrid/columns.tsx";
import i18n from "i18next";
import { getSalesLeadStateLabels } from "src/api/generated/erp/db/types/enums/salesLeadState.ts";
import { AavoTextField } from "src/components/common/inputFields/AavoTextField.tsx";
import { MenuCheckbox } from "src/components/common/contextMenu/MenuCheckbox.tsx";
import { SalesLeadForm } from "src/components/views/erp/sales/crm/unallocatedSalesLeads/SalesLeadForm.tsx";
import { SalesLeadsDataGridRowContextMenu } from "src/components/views/erp/sales/crm/salesLeads/SalesLeadsDataGridRowContextMenu.tsx";
import { useGenericDialog } from "src/components/common/dialogs/GenericDialogContext.ts";
import { SingleSalesLeadView } from "src/components/views/erp/sales/crm/salesLeads/SingleSalesLeadView.tsx";
import { Permissions } from "src/api/generated/iam/permissions.ts";
import { useHasPermission } from "src/components/views/erp/common/userPermissions.ts";
import { RefreshableElementRef } from "src/utils/useRefreshRef.ts";
import { defineAgentUiContextKey } from "src/components/views/ai/assistant/uiContext/agentUiContextKey.ts";
import {
	getSalesLeadFollowUpStatusLabels,
	SalesLeadFollowUpStatusIndicator,
} from "src/components/views/erp/sales/crm/salesLeads/SalesLeadFollowUpStatusIndicator.tsx";

export interface SalesLeadsDataGridFetchParams {
	searchQuery: string;
	showOpen: boolean;
	showDiscarded: boolean;
	showLost: boolean;
	showWon: boolean;
	customerId: number | undefined;
}

export interface SalesLeadsDataGridProps {
	refreshRef?: RefreshableElementRef<SalesLeadsDataGridFetchParams>;
	onSelectionChanged?: (rows: SalesLeadView[]) => void;
	onSalesLeadActivitiesChanged?: () => Promise<unknown> | void;
	onSalesLeadOffersChanged?: () => Promise<unknown> | void;
	selectFirstRowOnLoad?: boolean;
	customerId?: number;
}

export const SalesLeadsDataGrid = ({
	refreshRef,
	onSelectionChanged,
	onSalesLeadActivitiesChanged,
	onSalesLeadOffersChanged,
	selectFirstRowOnLoad = false,
	customerId,
}: SalesLeadsDataGridProps) => {
	const { openDialog } = useGenericDialog();
	const hasPermission = useHasPermission();
	const editable = hasPermission(Permissions.Sales.SalesLeads.MANAGE);
	return (
		<ServerSideDataGridModel
			fetchData={(params) => SalesLeadsApi.getAll(params)}
			getDataModelResult={(response) => response}
			refreshRef={refreshRef}
			initialParams={{
				searchQuery: "",
				showOpen: true,
				showDiscarded: false,
				showLost: false,
				showWon: false,
				customerId,
			}}
			getRowId={(row) => row.salesLeadId}
			customFunctionUiActionView={"SALES_LEADS"}
			gridId={"A3B7E2F1D9C40856"}
			onSelectionChanged={onSelectionChanged}
			selectFirstRowOnLoad={selectFirstRowOnLoad}
			render={({ dataGridProps, refreshData, currentParams }) => (
				<FormCrudDataGrid<SalesLeadView>
					actionBarComponents={
						<AavoTextField
							label={i18n.t("search")}
							onSubmit={(searchQuery) => refreshData({ searchQuery })}
						/>
					}
					actionBarMenuComponents={[
						<MenuCheckbox
							key={"showOpen"}
							label={i18n.t("show_open")}
							checked={currentParams.showOpen}
							onChange={(showOpen) => refreshData({ showOpen })}
						/>,
						<MenuCheckbox
							key={"showDiscarded"}
							label={i18n.t("show_discarded")}
							checked={currentParams.showDiscarded}
							onChange={(showDiscarded) => refreshData({ showDiscarded })}
						/>,
						<MenuCheckbox
							key={"showLost"}
							label={i18n.t("show_lost")}
							checked={currentParams.showLost}
							onChange={(showLost) => refreshData({ showLost })}
						/>,
						<MenuCheckbox
							key={"showWon"}
							label={i18n.t("show_won")}
							checked={currentParams.showWon}
							onChange={(showWon) => refreshData({ showWon })}
						/>,
					]}
					rowContextMenuComponents={({ row, allSelectedRows }) => (
						<SalesLeadsDataGridRowContextMenu
							key={"contextMenu"}
							row={row}
							allSelectedRows={allSelectedRows}
							refreshData={refreshData}
							onSalesLeadActivitiesChanged={onSalesLeadActivitiesChanged}
							onSalesLeadOffersChanged={onSalesLeadOffersChanged}
							editable={editable}
						/>
					)}
					openRow={(row) => {
						openDialog(() => ({
							title: `${i18n.t("sales_lead")} ${row.salesLeadId}`,
							size: "fullscreen",
							content: <SingleSalesLeadView salesLeadId={row.salesLeadId} />,
							onClose: refreshData,
							agentContext: {
								key: defineAgentUiContextKey("erp.sales.crm.salesLead.salesLeadsDataGrid.dialog"),
								title: i18n.t("sales_lead"),
								pathSegments: [`${i18n.t("sales_lead")} ${row.salesLeadId}`],
								data: {
									salesLeadId: row.salesLeadId,
									customerId: row.customerId ?? null,
									customerOrderOfferId: row.customerOrderOfferId ?? null,
								},
							},
						}));
					}}
					columns={[
						enumColumn({
							field: "followUpStatus",
							headerName: i18n.t("sales_lead_follow_up_status"),
							renderHeader: () => (
								<Tooltip title={i18n.t("sales_lead_follow_up_status")}>
									<FontAwesomeIcon icon={faTrafficLight} />
								</Tooltip>
							),
							enumLabels: getSalesLeadFollowUpStatusLabels(),
							width: 50,
							renderCell: ({ row }) => <SalesLeadFollowUpStatusIndicator status={row.followUpStatus} />,
							display: "flex",
							align: "center",
							headerAlign: "center",
						}),
						customerId == null &&
							textColumn({
								field: "customerName",
								headerName: i18n.t("customer_name"),
								width: 220,
							}),
						textColumn({
							field: "description",
							headerName: i18n.t("description"),
							width: 320,
						}),
						textColumn({
							field: "contactName",
							headerName: i18n.t("contact_person"),
							width: 170,
						}),
						textColumn({
							field: "contactEmail",
							headerName: i18n.t("email"),
							width: 230,
						}),
						textColumn({
							field: "contactPhone",
							headerName: i18n.t("phone"),
							width: 140,
						}),
						textColumn({
							field: "customerGroupName",
							headerName: i18n.t("customer_group"),
							width: 170,
						}),
						textColumn({
							field: "salesPipelineName",
							headerName: i18n.t("sales_pipeline"),
							width: 160,
						}),
						textColumn({
							field: "salesPipelinePhaseName",
							headerName: i18n.t("sales_pipeline_phase"),
							width: 190,
							renderCell: ({ row }) => (
								<PhasePill name={row.salesPipelinePhaseName} color={row.salesPipelinePhaseColor} />
							),
							display: "flex",
						}),
						enumColumn({
							field: "salesLeadState",
							headerName: i18n.t("state"),
							enumLabels: getSalesLeadStateLabels(),
							width: 120,
						}),
						textColumn({
							field: "salespersonName",
							headerName: i18n.t("salesperson"),
							width: 170,
						}),
						textColumn({
							field: "salesAreaName",
							headerName: i18n.t("sales_area"),
							width: 160,
						}),
						textColumn({
							field: "salesLeadSourceName",
							headerName: i18n.t("lead_source"),
							width: 160,
						}),
						textColumn({
							field: "fullAddress",
							headerName: i18n.t("address"),
							width: 300,
						}),
						dateTimeColumn({
							field: "createdAt",
							headerName: i18n.t("created_at"),
							width: 170,
						}),
					]}
					form={{
						addRowEnabled: editable,
						editEnabled: editable,
						dialogSize: "lg",
						dialogTitle: i18n.t("sales_lead"),
						agentContext: (row) => ({
							key: defineAgentUiContextKey("erp.sales.crm.salesLead.formDialog"),
							data: {
								salesLeadId: row?.salesLeadId ?? null,
								customerId: row?.customerId ?? customerId ?? null,
							},
							pathSegments:
								row?.salesLeadId ?
									[`${i18n.t("sales_lead")} ${row.salesLeadId}`]
								:	[i18n.t("sales_lead")],
						}),
						component: ({ row, onCompleted, onFormEdited }) => (
							<SalesLeadForm
								initialization={
									row != null ?
										{
											type: "existingLead",
											salesLeadId: row.salesLeadId,
										}
									:	{
											type: "newLead",
											customerId,
											placeOnPipeline: customerId != null,
											assignCurrentSalesperson: true,
										}
								}
								onCompleted={onCompleted}
								onFormEdited={onFormEdited}
							/>
						),
					}}
					remove={
						editable ?
							async ({ items }) => {
								await SalesLeadsApi.delete_({
									salesLeadIds: items.map((item) => item.salesLeadId),
								});
							}
						:	undefined
					}
					agentContext={{
						key: defineAgentUiContextKey("erp.sales.crm.salesLeads.grid"),
						data: { customerId: customerId ?? null },
						selectedRowPathSegment: (row) => `${i18n.t("sales_lead")} ${row.salesLeadId}`,
						rowFields: ["salesLeadId", "customerId", "customerOrderOfferId", "description"],
					}}
					{...dataGridProps}
				/>
			)}
		/>
	);
};

interface PhasePillProps {
	name: string | null | undefined;
	color: string | null | undefined;
}

const PhasePill = ({ name, color }: PhasePillProps) => {
	if (!name) return null;
	return (
		<Box
			sx={{
				border: 1,
				borderColor: color ?? "transparent",
				borderRadius: "9999px",
				px: 1.5,
				py: 0.25,
				whiteSpace: "nowrap",
				overflow: "hidden",
				textOverflow: "ellipsis",
				maxWidth: "100%",
			}}
		>
			{name}
		</Box>
	);
};
